// ─────────────────────────────────────────────────────────────────────────────
// BoneFlow billing utilities
//
// FUTURE FRONTEND WORK (not built yet — backend/data layer only so far):
//   1. Company-facing billing/subscription page — shows the company's current
//      plan (via resolveCompanyBilling), Cruncher usage (via the
//      getCruncherUsageForBilling callable), and upgrade/manage buttons that
//      call the createCheckoutSession / createCustomerPortalSession callables
//      and redirect to the returned Stripe URL.
//   2. Platform-admin dashboard — BoneFlow's internal company-monitoring view:
//      all companies with their subscriptionStatus, plus editors for
//      billingPlans documents and per-company billingOverrides.
//   3. ~~platformAdmin permission/role system~~ — DONE. users/{uid}.
//      platformAdmin now gates seedBillingPlans / backfillCompanyBillingFields
//      / cross-company usage via functions/platformAdmin.js (the platform
//      owner's verified auth email remains only as a bootstrap fallback).
//      See src/utils/internalCompany.js for the frontend half.
//
// Data model recap:
//   billingPlans/{planId}   — plan TEMPLATES, editable in Firestore (never
//                             hardcoded in functions) so pricing can change
//                             without a redeploy. Fields: name, stripePriceId,
//                             basePrice, includedUsers, perUserOveragePrice,
//                             includedCruncherCredits (and the LEGACY
//                             includedCruncherRuns), cruncherOverageCreditPrice
//                             (and the LEGACY per-RUN cruncherOveragePrice),
//                             isActive, createdAt, updatedAt. All prices are
//                             integer CENTS (29900 = $299.00 — the Base
//                             plan's real seeded price; see SEED_PLANS in
//                             functions/billing.js).
//   companies/{companyId}   — billingPlanId points at a template;
//                             billingOverrides optionally overrides individual
//                             template fields for just that company. Stripe
//                             state (stripeCustomerId, stripeSubscriptionId,
//                             subscriptionStatus, subscriptionCurrentPeriodEnd)
//                             is server-owned: firestore.rules denies client
//                             writes to all of these fields — Cloud Functions
//                             (functions/billing.js) are the only writers.
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_BILLING_PLAN_ID = 'base';
export const DEFAULT_SUBSCRIPTION_STATUS = 'trialing';

// Override keys that are silently ignored: identity/audit fields of the plan
// template. Letting a company "override" the template's id or timestamps has
// no meaning and would only corrupt the resolved output.
const NON_OVERRIDABLE_FIELDS = ['id', 'createdAt', 'updatedAt'];

/**
 * resolveCompanyBilling — THE single source of truth for what a company pays.
 *
 * NOTE: functions/billing.js contains a mirror of this function (Cloud
 * Functions deploys only the functions/ directory, so it can't import this
 * module). If you change the merge semantics here, change them there too.
 *
 * Takes a company document and the billingPlans collection data and returns
 * the company's EFFECTIVE billing configuration:
 *
 *   1. Pick the plan template named by company.billingPlanId
 *      (missing/empty → DEFAULT_BILLING_PLAN_ID 'base'; an id that matches no
 *      template also falls back to 'base' so a deleted plan can't brick a
 *      company).
 *   2. Shallow-merge company.billingOverrides on top of the template.
 *      Overrides win field-by-field: { basePrice: 25000 } changes ONLY
 *      basePrice — every other field still inherits live from the template,
 *      so later edits to the template flow through automatically.
 *      Keys in NON_OVERRIDABLE_FIELDS, null, and undefined values are ignored
 *      (null means "no override", never "override with nothing").
 *
 * Anything that needs to know a company's pricing — frontend billing pages,
 * the admin dashboard, overage calculations in Cloud Functions — must go
 * through this merge. Never read billingPlans or billingOverrides directly.
 *
 * IMPORTANT Stripe caveat: what Stripe actually CHARGES is driven by
 * stripePriceId, not by basePrice. basePrice (and the other numeric fields)
 * are BoneFlow's own record used for display and overage math. A company with
 * a custom price therefore needs BOTH billingOverrides.basePrice (so the app
 * shows the right number) AND billingOverrides.stripePriceId pointing at a
 * matching custom Stripe Price (so Stripe bills the right number).
 *
 * @param {object|null} company
 *   The companies/{id} document data. Reads billingPlanId, billingOverrides,
 *   and subscriptionStatus; all are optional (defaults applied).
 * @param {object|Array|null} billingPlans
 *   The billingPlans collection, either as an array of docs
 *   ([{ id: 'base', ... }, ...]) or a map keyed by plan id
 *   ({ base: {...}, growth: {...} }). Both shapes are accepted so callers can
 *   pass whatever their Firestore read produced.
 * @returns {object|null}
 *   null when company is missing or no usable template exists (e.g. the
 *   billingPlans collection hasn't been seeded). Otherwise:
 *   {
 *     planId: string,               // id of the template actually used
 *     name: string,                 // plan display name (overridable)
 *     stripePriceId: string,        // Stripe Price the subscription bills on
 *     basePrice: number,            // monthly base, in cents
 *     includedUsers: number,        // seats included in basePrice
 *     perUserOveragePrice: number,  // cents per user beyond includedUsers
 *     includedCruncherCredits: number, // Cruncher CREDITS included per month
 *     includedCruncherRuns: number,    // LEGACY — runs, converted if credits
 *                                      // is absent. See cruncherAllowance.
 *     cruncherOveragePrice: number, // cents per run beyond included
 *     isActive: boolean,            // template/override active flag
 *     hasOverrides: boolean,        // true if any override was applied
 *     overriddenFields: string[],   // which fields came from overrides
 *     subscriptionStatus: string,   // company's live status ('trialing',
 *                                   // 'active', 'past_due', 'canceled',
 *                                   // 'incomplete'); defaults to 'trialing'
 *   }
 */
export function resolveCompanyBilling(company, billingPlans) {
  if (!company) return null;

  // Normalize both accepted billingPlans shapes into a map keyed by plan id.
  const plansById = Array.isArray(billingPlans)
    ? Object.fromEntries(billingPlans.filter(p => p && p.id).map(p => [p.id, p]))
    : (billingPlans || {});

  const planId = (typeof company.billingPlanId === 'string' && company.billingPlanId)
    ? company.billingPlanId
    : DEFAULT_BILLING_PLAN_ID;

  const template = plansById[planId] || plansById[DEFAULT_BILLING_PLAN_ID] || null;
  if (!template) return null;

  const overrides = (company.billingOverrides
    && typeof company.billingOverrides === 'object'
    && !Array.isArray(company.billingOverrides))
    ? company.billingOverrides
    : {};

  const effective = { ...template };
  const overriddenFields = [];
  Object.keys(overrides).forEach((key) => {
    if (NON_OVERRIDABLE_FIELDS.includes(key)) return;
    if (overrides[key] === undefined || overrides[key] === null) return;
    effective[key] = overrides[key];
    overriddenFields.push(key);
  });

  return {
    planId: template.id,
    name: typeof effective.name === 'string' ? effective.name : '',
    stripePriceId: typeof effective.stripePriceId === 'string' ? effective.stripePriceId : '',
    basePrice: Number(effective.basePrice) || 0,
    includedUsers: Number(effective.includedUsers) || 0,
    perUserOveragePrice: Number(effective.perUserOveragePrice) || 0,
    // Both units pass through. includedCruncherCredits is the live allowance;
    // includedCruncherRuns is LEGACY and only reached by cruncherAllowance when
    // a plan document predates credits. Dropping either here would silently
    // send that fallback down the wrong branch.
    includedCruncherCredits: Number(effective.includedCruncherCredits) || 0,
    includedCruncherRuns: Number(effective.includedCruncherRuns) || 0,
    cruncherOverageCreditPrice: Number(effective.cruncherOverageCreditPrice) || 0,
    cruncherOveragePrice: Number(effective.cruncherOveragePrice) || 0,
    isActive: effective.isActive === true,
    hasOverrides: overriddenFields.length > 0,
    overriddenFields,
    subscriptionStatus: company.subscriptionStatus || DEFAULT_SUBSCRIPTION_STATUS,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// ── CRUNCHER CREDIT WEIGHTS — THE TUNABLE BLOCK ────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════
//
// EVERYTHING MATT MIGHT WANT TO CHANGE ABOUT CRUNCHER PRICING IS IN THIS ONE
// BLOCK AND ITS MIRROR IN functions/billing.js. Nothing else in the app hard-
// codes a weight, a budget, or a run cost.
//
// ── WHY CREDITS AND NOT RUNS ────────────────────────────────────────────────
// A flat run count says every Cruncher run costs the same. It does not: the
// tools call different models with different payloads, and the spread is real
// money. Credits let the allowance track cost instead of button presses.
//
// ── WHAT EACH TOOL ACTUALLY SENDS (read from functions/index.js, 2026-09-14) ─
//
//   GENERAL CRUNCHER  (cruncherProxy action 'summarizeDocument')
//     model     claude-opus-4-5 — $5.00 / MTok in, $25.00 / MTok out
//     payload   THE WHOLE UPLOADED FILE, as a base64 `document` block (PDF) or
//               `image` block. Nothing is extracted or trimmed first.
//     note      A PDF document block is billed per page as 1,500-3,000 TEXT
//               tokens PLUS image tokens, because the API also converts every
//               page to an image (platform.claude.com PDF support). So this is
//               the VISION-heavy path, on the most expensive model, and its
//               cost scales linearly with page count with no cap.
//     output    max_tokens 4000; a realistic JSON answer is ~900.
//
//   PLAN CRUNCHER  (extractAndCrunch → generateBoneflowedPlan)
//     models    claude-haiku-4-5 ($1 / $5) then claude-sonnet-4-5 ($3 / $15)
//     payload   TEXT ONLY. pdf-parse extracts each page's text on the server;
//               no image ever reaches the API.
//     stage 1   one Haiku call per page, pageText.slice(0, 6000) ≈ 1.5K tokens
//               in, max_tokens 10 out. Scales with pages.
//     stage 2   ONE Sonnet call, sourceForSonnet.slice(0, 100000) ≈ 25K tokens
//               in, 4000 max out. CAPPED — a 300-page set costs the same here
//               as a 40-page one.
//     note      generateBoneflowedPlan, the second half of the pipeline, is
//               pure pdf-lib assembly and makes NO AI call at all. (Its
//               ANTHROPIC_KEY secret declaration is vestigial.)
//
//   FLOOR PLAN CRUNCHER  (generateFloorPlanSVG)
//     NOT SHIPPED. The UI tile is FloorPlanCruncherStub — "Coming soon" — and
//     is `hidden: true` in the Cruncher page's TOOLS list. generateFloorPlanSVG
//     exists server-side but has ZERO callers in src/. Its weight below is a
//     placeholder for the day it ships and should be re-derived then.
//
// ── THE ARITHMETIC, AT PUBLISHED RATES ──────────────────────────────────────
// Model prices fetched from platform.claude.com on 2026-09-14. Page counts and
// output sizes are ESTIMATES of typical documents — they are the soft part of
// this model and the first thing to revisit.
//
//   General Cruncher, an 8-page contract/NOA/spec report:
//     in   8 pages × ~3,000 tok (text + image)  = 24,000 × $5/M  = $0.120
//     out  ~900 tok                                       × $25/M = $0.023
//                                                          TOTAL ≈ $0.14
//
//   Plan Cruncher, a 40-page architectural plan set:
//     Haiku   40 calls × ~1,550 tok in = 62,000 × $1/M  = $0.062
//             40 × 10 tok out                  × $5/M  = $0.002
//     Sonnet  ~25,000 tok in            × $3/M          = $0.075
//             ~2,000 tok out            × $15/M         = $0.030
//                                                  TOTAL ≈ $0.17
//
// ── THE FINDING, AND IT INVERTS THE USUAL ASSUMPTION ────────────────────────
// The intuition is that a multi-page plan extraction dwarfs a quick text
// summary. In THIS codebase the opposite is nearly true: the General Cruncher
// is the one sending images to Opus, and the Plan Cruncher is the one that
// strips to text and uses the two cheapest models. They land within ~1.2× of
// each other on typical documents.
//
// PAGES, NOT TOOL, ARE THE REAL COST DRIVER. A 20-page General Cruncher upload
// is ~$0.32 — more than any Plan Cruncher run — while a 3-page one is ~$0.07.
// The Plan Cruncher is the FLATTER of the two because its expensive stage is
// capped at 100K characters. A per-tool weight is therefore a deliberate
// simplification: it is honest about the average and blind to the spread. See
// the vision-doc entry for what a per-page term would take.
//
// ── THE ANCHOR ──────────────────────────────────────────────────────────────
// One credit ≈ $0.05 of Anthropic spend. Chosen so typical runs land on small
// whole numbers a human can reason about, and so a budget in credits can be
// read straight back as a dollar exposure: credits × $0.05 ≈ worst-case AI
// cost for the month.
export const CREDIT_USD = 0.05;

// Credits charged per run, keyed by the `tool` the client reports. Change these
// freely — the counter, the enforcement and both usage bars all read them.
export const CRUNCHER_TOOL_CREDITS = {
  general: 3,     // ≈ $0.14 at 8 pages. Opus + vision, scales with pages.
  plan: 4,        // ≈ $0.17 at 40 pages. Haiku ×pages + one capped Sonnet.
  floorplan: 5,   // PROVISIONAL — not shipped. See above; re-derive at launch.
};

// What an unrecognised or missing tool costs. Deliberately the GENERAL weight
// rather than 1: an unknown run is far more likely to be a real AI call whose
// label drifted than a free one, and undercharging is the failure that costs
// money rather than the one that annoys a user.
export const CRUNCHER_DEFAULT_CREDITS = 3;

/**
 * Credits a single run of `tool` costs. The one place the weight table is read.
 * @param {string} tool  'general' | 'plan' | 'floorplan'
 * @returns {number} a positive integer
 */
export function cruncherRunCredits(tool) {
  const w = CRUNCHER_TOOL_CREDITS[String(tool || '').trim()];
  return Number.isFinite(w) && w > 0 ? w : CRUNCHER_DEFAULT_CREDITS;
}

// ── THE CRUNCHER ALLOWANCE: ONE DENOMINATOR ─────────────────────────────────
//
// A percentage is only as honest as what it divides by, and until 2026-09-14
// this app had TWO answers to "how many Cruncher runs does this company get"
// that disagreed with each other:
//
//   • companies/{id}.monthlyRunLimit, defaulting to 50 — read by the Cruncher
//     page's bar AND by readUsageAndLimit() in functions/index.js, which is
//     what actually HARD-BLOCKS a run at the cap.
//   • resolveCompanyBilling().includedCruncherRuns — read by Settings →
//     Billing & Plan, and the figure cruncherOveragePrice is charged against.
//
// For a Base company both say 50 and nobody noticed. For a GROWTH company —
// includedCruncherRuns 150, monthlyRunLimit unset so the default 50 applies —
// Settings promised 150 runs, the Cruncher page said "of 50", and the server
// stopped them at 50. A customer paying for Growth got a third of what the
// plan grants.
//
// THE PRECEDENCE, and every step of it is chosen so no company can end up with
// LESS than it has today:
//
// THE UNIT IS NOW CREDITS (2026-09-14, second pass). Runs were the wrong unit
// for the same reason two sources were the wrong count: they imply every run
// costs the same, and the measured spread between tools and document sizes says
// otherwise. See the CRUNCHER CREDIT WEIGHTS block above for the arithmetic.
//
// ── THE MONTHLY BUDGETS ─────────────────────────────────────────────────────
// Seeded onto the plan templates as `includedCruncherCredits` (SEED_PLANS in
// functions/billing.js). Restated here because this is the file someone opens
// to ask "what does a company get".
//
//   BASE    200 credits ≈ 67 General runs, or 50 Plan runs, or a mix.
//   GROWTH  750 credits ≈ 250 General runs, or 187 Plan runs. 3.75x Base.
//
// A realistic Base month — 30 documents through the General Cruncher and 15
// plan sets through the Plan Cruncher — is 30x3 + 15x4 = 150 of 200, leaving
// real headroom for a busy month rather than a cliff. The same shape at Growth
// volume — 100 and 60 — is 540 of 750.
//
// WHAT IT COSTS BONEFLOW, which is the number that decides whether these are
// sane: that Base month is ~$6.75 of Anthropic spend, and a Base company that
// burns all 200 credits costs ~$10 — 3.3% of the $299 plan. A Growth company
// that burns all 750 costs ~$37.50 — 9.4% of $399. Growth is deliberately the
// larger share: it is the tier that should feel uncapped.
//
// ── THE PRECEDENCE, and every step is chosen so no company ends up with LESS
//    CAPACITY THAN IT HAS TODAY ────────────────────────────────────────────
//
//   1. An explicit, positive company.monthlyCreditLimit — a deliberate
//      per-company cap in the new unit.
//   2. An explicit, positive company.monthlyRunLimit — the LEGACY per-company
//      cap. Still honoured, converted at CRUNCHER_DEFAULT_CREDITS per run, so a
//      company someone hand-capped years ago keeps exactly the capacity that
//      cap was meant to give.
//   3. The plan's includedCruncherCredits, through resolveCompanyBilling so
//      billingOverrides apply — the same single-source rule as every other
//      billing question.
//   4. The plan's legacy includedCruncherRuns, converted the same way. THIS IS
//      WHAT MAKES THE MIGRATION OPTIONAL: a billingPlans document that never
//      gains the credits field keeps working and keeps its capacity, which
//      matters because seedBillingPlans is idempotent and cannot rewrite an
//      environment that was already seeded.
//   5. CRUNCHER_CREDIT_FLOOR — the old 50-run floor, in credits.
//
// A ZERO IS TREATED AS "UNSET", NOT AS "NONE", and that is deliberate. The
// 'custom' plan template ships all-zeros as a starting point for per-company
// pricing (see SEED_PLANS), so a company parked on it would resolve to 0 —
// and reading that literally would silently cut off every run for a customer
// whose real terms live in billingOverrides. There is no way to express "no
// credits included" in this data model today; inventing one here, by accident,
// as a side effect of a metering change, is not the place to do it.

// What one legacy RUN is worth in credits. The default weight rather than the
// average of the table: a legacy figure was written when every run counted the
// same, so the honest translation is "that many runs of an ordinary tool".
export const LEGACY_RUN_CREDITS = CRUNCHER_DEFAULT_CREDITS;

// The floor, in credits: the 50 runs functions/index.js has always defaulted to.
export const CRUNCHER_CREDIT_FLOOR = 50 * LEGACY_RUN_CREDITS;

/**
 * The company's effective monthly Cruncher CREDIT allowance — the denominator
 * for every usage percentage in the app, and the cap functions/index.js
 * enforces.
 *
 * @param {object|null} company        companies/{id}
 * @param {object|Array|null} billingPlans  the billingPlans collection
 * @returns {number} a positive integer, in credits
 */
export function cruncherAllowance(company, billingPlans) {
  const explicitCredits = Number(company?.monthlyCreditLimit);
  if (Number.isFinite(explicitCredits) && explicitCredits > 0) return explicitCredits;
  const explicitRuns = Number(company?.monthlyRunLimit);
  if (Number.isFinite(explicitRuns) && explicitRuns > 0) return explicitRuns * LEGACY_RUN_CREDITS;
  // resolveCompanyBilling returns NULL on an unseeded collection — which is
  // exactly the state the Cruncher page is in before its lazy fetch resolves.
  const resolved = resolveCompanyBilling(company, billingPlans);
  const credits = Number(resolved && resolved.includedCruncherCredits);
  if (Number.isFinite(credits) && credits > 0) return credits;
  const legacyRuns = Number(resolved && resolved.includedCruncherRuns);
  if (Number.isFinite(legacyRuns) && legacyRuns > 0) return legacyRuns * LEGACY_RUN_CREDITS;
  return CRUNCHER_CREDIT_FLOOR;
}

/**
 * Usage as a percentage of the allowance, for the bar and its label.
 * Returns { used, limit, pct, pctCapped, over } — `pct` is the true figure
 * (which can exceed 100), `pctCapped` is what a bar's width should use.
 */
export function cruncherUsagePercent(used, limit) {
  const u = Math.max(0, Number(used) || 0);
  const l = Math.max(1, Number(limit) || CRUNCHER_CREDIT_FLOOR);
  const pct = Math.round((u / l) * 100);
  return { used: u, limit: l, pct, pctCapped: Math.min(100, pct), over: Math.max(0, u - l) };
}

/**
 * Format an integer cent amount for display, e.g. 29900 → "$299" and
 * 1250 → "$12.50". Whole-dollar amounts drop the cents for cleaner pricing UI.
 */
export function formatCents(cents) {
  const dollars = (Number(cents) || 0) / 100;
  return Number.isInteger(dollars)
    ? `$${dollars.toLocaleString('en-US')}`
    : `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
